import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator/index.js";
import { HttpBackend, HttpResponseError, STATUS_CODE } from '@taquito/http-utils';
import BigNumber from 'bignumber.js';
import { validateAddress as _validateAddress, ValidationResult, InvalidAddressError, validateContractAddress, InvalidContractAddressError } from '@taquito/utils';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

var defaultChain = 'main';
var defaultRPCOptions = {
  block: 'head'
};
/**
 * Casts object/array items to BigNumber
 * @param data input object or array
 * @param keys keys for processing or all items if not defined
 *
 */

function castToBigNumber(data, keys) {
  var returnArray = Array.isArray(data);

  if (typeof keys === 'undefined') {
    keys = Object.keys(data);
  }

  var response = returnArray ? [] : {};
  keys.forEach(function (key) {
    var item = data[key];
    var res;

    if (typeof item === 'undefined') {
      return;
    }

    if (Array.isArray(item)) {
      res = castToBigNumber(item);
      response[key] = res;
      return;
    }

    res = new BigNumber(item);
    response[key] = res;
  });
  return response;
}

var defaultTtl = 1000;
/***
 * @description RpcClientCache acts as a decorator over the RpcClient instance by caching responses for the period defined by the ttl.
 */

var RpcClientCache = /*#__PURE__*/function () {
  /**
   *
   * @param rpcClient rpcClient responsible of the interaction with Tezos network through an rpc node
   * @param ttl number representing the time to live (default 1000 milliseconds)
   *
   * @example new RpcClientCache(new RpcClient('https://mainnet.api.tez.ie/'))
   */
  function RpcClientCache(rpcClient) {
    var ttl = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultTtl;

    _classCallCheck(this, RpcClientCache);

    this.rpcClient = rpcClient;
    this.ttl = ttl;
    this._cache = {};
  }

  _createClass(RpcClientCache, [{
    key: "getAllCachedData",
    value: function getAllCachedData() {
      return this._cache;
    }
    /**
     * @description Remove all the data in the cache.
     *
     */

  }, {
    key: "deleteAllCachedData",
    value: function deleteAllCachedData() {
      for (var key in this._cache) {
        delete this._cache[key];
      }
    }
  }, {
    key: "formatCacheKey",
    value: function formatCacheKey(rpcUrl, rpcMethodName, rpcMethodParams, rpcMethodData) {
      var paramsToString = '';
      rpcMethodParams.forEach(function (param) {
        paramsToString = typeof param === 'object' ? paramsToString + JSON.stringify(param) + '/' : paramsToString + param + '/';
      });
      return rpcMethodData ? "".concat(rpcUrl, "/").concat(rpcMethodName, "/").concat(paramsToString, "/").concat(JSON.stringify(rpcMethodData)) : "".concat(rpcUrl, "/").concat(rpcMethodName, "/").concat(paramsToString);
    }
  }, {
    key: "has",
    value: function has(key) {
      return key in this._cache;
    }
  }, {
    key: "get",
    value: function get(key) {
      return this._cache[key].response;
    }
  }, {
    key: "put",
    value: function put(key, response) {
      var _this = this;

      var handle = setTimeout(function () {
        return _this.remove(key);
      }, this.ttl);
      Object.assign(this._cache, _defineProperty({}, key, {
        handle: handle,
        response: response
      }));
    }
  }, {
    key: "remove",
    value: function remove(key) {
      if (key in this._cache) {
        delete this._cache[key];
      }
    }
  }, {
    key: "validateAddress",
    value: function validateAddress(address) {
      if (_validateAddress(address) !== ValidationResult.VALID) {
        throw new InvalidAddressError("Invalid address: ".concat(address));
      }
    }
  }, {
    key: "validateContract",
    value: function validateContract(address) {
      if (validateContractAddress(address) !== ValidationResult.VALID) {
        throw new InvalidContractAddressError("Invalid contract address: ".concat(address));
      }
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Get the block's hash, its unique identifier.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-hash
     */

  }, {
    key: "getBlockHash",
    value: function getBlockHash() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBlockHash', [block]);

                if (!this.has(key)) {
                  _context.next = 5;
                  break;
                }

                return _context.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBlockHash({
                  block: block
                });
                this.put(key, response);
                return _context.abrupt("return", response);

              case 8:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List the ancestors of the given block which, if referred to as the branch in an operation header, are recent enough for that operation to be included in the current block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-live-blocks
     */

  }, {
    key: "getLiveBlocks",
    value: function getLiveBlocks() {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref2.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getLiveBlocks', [block]);

                if (!this.has(key)) {
                  _context2.next = 5;
                  break;
                }

                return _context2.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getLiveBlocks({
                  block: block
                });
                this.put(key, response);
                return _context2.abrupt("return", response);

              case 8:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));
    }
    /**
     *
     * @param address address from which we want to retrieve the balance
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the balance of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-balance
     */

  }, {
    key: "getBalance",
    value: function getBalance(address) {
      var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref3.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                this.validateAddress(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBalance', [block, address]);

                if (!this.has(key)) {
                  _context3.next = 6;
                  break;
                }

                return _context3.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getBalance(address, {
                  block: block
                });
                this.put(key, response);
                return _context3.abrupt("return", response);

              case 9:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the storage
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the data of the contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-storage
     */

  }, {
    key: "getStorage",
    value: function getStorage(address) {
      var _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref4.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) {
            switch (_context4.prev = _context4.next) {
              case 0:
                this.validateContract(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getStorage', [block, address]);

                if (!this.has(key)) {
                  _context4.next = 6;
                  break;
                }

                return _context4.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getStorage(address, {
                  block: block
                });
                this.put(key, response);
                return _context4.abrupt("return", response);

              case 9:
              case "end":
                return _context4.stop();
            }
          }
        }, _callee4, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the script
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the code and data of the contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-script
     */

  }, {
    key: "getScript",
    value: function getScript(address) {
      var _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref5.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) {
            switch (_context5.prev = _context5.next) {
              case 0:
                this.validateContract(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getScript', [block, address]);

                if (!this.has(key)) {
                  _context5.next = 6;
                  break;
                }

                return _context5.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getScript(address, {
                  block: block
                });
                this.put(key, response);
                return _context5.abrupt("return", response);

              case 9:
              case "end":
                return _context5.stop();
            }
          }
        }, _callee5, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the script
     * @param unparsingMode default is { unparsing_mode: "Readable" }
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the script of the contract and normalize it using the requested unparsing mode.
     *
     */

  }, {
    key: "getNormalizedScript",
    value: function getNormalizedScript(address) {
      var unparsingMode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        unparsing_mode: 'Readable'
      };

      var _ref6 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref6.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                this.validateContract(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getNormalizedScript', [block, address, unparsingMode]);

                if (!this.has(key)) {
                  _context6.next = 6;
                  break;
                }

                return _context6.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getNormalizedScript(address, unparsingMode, {
                  block: block
                });
                this.put(key, response);
                return _context6.abrupt("return", response);

              case 9:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the complete status of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id
     */

  }, {
    key: "getContract",
    value: function getContract(address) {
      var _ref7 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref7.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                this.validateAddress(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getContract', [block, address]);

                if (!this.has(key)) {
                  _context7.next = 6;
                  break;
                }

                return _context7.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getContract(address, {
                  block: block
                });
                this.put(key, response);
                return _context7.abrupt("return", response);

              case 9:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the manager
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the manager key of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-manager-key
     */

  }, {
    key: "getManagerKey",
    value: function getManagerKey(address) {
      var _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref8.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                this.validateAddress(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getManagerKey', [block, address]);

                if (!this.has(key)) {
                  _context8.next = 6;
                  break;
                }

                return _context8.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getManagerKey(address, {
                  block: block
                });
                this.put(key, response);
                return _context8.abrupt("return", response);

              case 9:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the delegate (baker)
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the delegate of a contract, if any.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-delegate
     */

  }, {
    key: "getDelegate",
    value: function getDelegate(address) {
      var _ref9 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref9.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee9() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) {
            switch (_context9.prev = _context9.next) {
              case 0:
                this.validateAddress(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getDelegate', [block, address]);

                if (!this.has(key)) {
                  _context9.next = 6;
                  break;
                }

                return _context9.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getDelegate(address, {
                  block: block
                });
                this.put(key, response);
                return _context9.abrupt("return", response);

              case 9:
              case "end":
                return _context9.stop();
            }
          }
        }, _callee9, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the big map key
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a key in the big map storage of the contract.
     *
     * @deprecated Deprecated in favor of getBigMapKeyByID
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-context-contracts-contract-id-big-map-get
     */

  }, {
    key: "getBigMapKey",
    value: function getBigMapKey(address, key) {
      var _ref10 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref10.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee10() {
        var keyUrl, response;
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) {
            switch (_context10.prev = _context10.next) {
              case 0:
                this.validateAddress(address);
                keyUrl = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBigMapKey', [block, address, key]);

                if (!this.has(keyUrl)) {
                  _context10.next = 6;
                  break;
                }

                return _context10.abrupt("return", this.get(keyUrl));

              case 6:
                response = this.rpcClient.getBigMapKey(address, key, {
                  block: block
                });
                this.put(keyUrl, response);
                return _context10.abrupt("return", response);

              case 9:
              case "end":
                return _context10.stop();
            }
          }
        }, _callee10, this);
      }));
    }
    /**
     *
     * @param id Big Map ID
     * @param expr Expression hash to query (A b58check encoded Blake2b hash of the expression (The expression can be packed using the pack_data method))
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a key in a big map.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-big-maps-big-map-id-script-expr
     */

  }, {
    key: "getBigMapExpr",
    value: function getBigMapExpr(id, expr) {
      var _ref11 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref11.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee11() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) {
            switch (_context11.prev = _context11.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBigMapExpr', [block, id, expr]);

                if (!this.has(key)) {
                  _context11.next = 5;
                  break;
                }

                return _context11.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBigMapExpr(id, expr, {
                  block: block
                });
                this.put(key, response);
                return _context11.abrupt("return", response);

              case 8:
              case "end":
                return _context11.stop();
            }
          }
        }, _callee11, this);
      }));
    }
    /**
     *
     * @param address delegate address which we want to retrieve
     * @param options contains generic configuration for rpc calls
     *
     * @description Fetches information about a delegate from RPC.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-delegates-pkh
     */

  }, {
    key: "getDelegates",
    value: function getDelegates(address) {
      var _ref12 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref12.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee12() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) {
            switch (_context12.prev = _context12.next) {
              case 0:
                this.validateAddress(address);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getDelegates', [block, address]);

                if (!this.has(key)) {
                  _context12.next = 6;
                  break;
                }

                return _context12.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getDelegates(address, {
                  block: block
                });
                this.put(key, response);
                return _context12.abrupt("return", response);

              case 9:
              case "end":
                return _context12.stop();
            }
          }
        }, _callee12, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description All constants
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-constants
     */

  }, {
    key: "getConstants",
    value: function getConstants() {
      var _ref13 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref13.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) {
            switch (_context13.prev = _context13.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getConstants', [block]);

                if (!this.has(key)) {
                  _context13.next = 5;
                  break;
                }

                return _context13.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getConstants({
                  block: block
                });
                this.put(key, response);
                return _context13.abrupt("return", response);

              case 8:
              case "end":
                return _context13.stop();
            }
          }
        }, _callee13, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls. See examples for various available sytaxes.
     *
     * @description All the information about a block
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id
     * @example getBlock() will default to /main/chains/block/head.
     * @example getBlock({ block: head~2 }) will return an offset of 2 blocks.
     * @example getBlock({ block: BL8fTiWcSxWCjiMVnDkbh6EuhqVPZzgWheJ2dqwrxYRm9AephXh~2 }) will return an offset of 2 blocks from given block hash..
     */

  }, {
    key: "getBlock",
    value: function getBlock() {
      var _ref14 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref14.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee14() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) {
            switch (_context14.prev = _context14.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBlock', [block]);

                if (!this.has(key)) {
                  _context14.next = 5;
                  break;
                }

                return _context14.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBlock({
                  block: block
                });
                this.put(key, response);
                return _context14.abrupt("return", response);

              case 8:
              case "end":
                return _context14.stop();
            }
          }
        }, _callee14, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description The whole block header
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-header
     */

  }, {
    key: "getBlockHeader",
    value: function getBlockHeader() {
      var _ref15 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref15.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee15() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) {
            switch (_context15.prev = _context15.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBlockHeader', [block]);

                if (!this.has(key)) {
                  _context15.next = 5;
                  break;
                }

                return _context15.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBlockHeader({
                  block: block
                });
                this.put(key, response);
                return _context15.abrupt("return", response);

              case 8:
              case "end":
                return _context15.stop();
            }
          }
        }, _callee15, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description All the metadata associated to the block
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-metadata
     */

  }, {
    key: "getBlockMetadata",
    value: function getBlockMetadata() {
      var _ref16 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref16.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee16() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) {
            switch (_context16.prev = _context16.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBlockMetadata', [block]);

                if (!this.has(key)) {
                  _context16.next = 5;
                  break;
                }

                return _context16.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBlockMetadata({
                  block: block
                });
                this.put(key, response);
                return _context16.abrupt("return", response);

              case 8:
              case "end":
                return _context16.stop();
            }
          }
        }, _callee16, this);
      }));
    }
    /**
     *
     * @param args contains optional query arguments
     * @param options contains generic configuration for rpc calls
     *
     * @description Retrieves the list of delegates allowed to bake a block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-helpers-baking-rights
     */

  }, {
    key: "getBakingRights",
    value: function getBakingRights() {
      var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _ref17 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref17.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee17$(_context17) {
          while (1) {
            switch (_context17.prev = _context17.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBakingRights', [block, args]);

                if (!this.has(key)) {
                  _context17.next = 5;
                  break;
                }

                return _context17.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBakingRights(args, {
                  block: block
                });
                this.put(key, response);
                return _context17.abrupt("return", response);

              case 8:
              case "end":
                return _context17.stop();
            }
          }
        }, _callee17, this);
      }));
    }
    /**
     *
     * @param args contains optional query arguments
     * @param options contains generic configuration for rpc calls
     *
     * @description Retrieves the list of delegates allowed to bake a block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-helpers-endorsing-rights
     */

  }, {
    key: "getEndorsingRights",
    value: function getEndorsingRights() {
      var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _ref18 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref18.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee18() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee18$(_context18) {
          while (1) {
            switch (_context18.prev = _context18.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getEndorsingRights', [block, args]);

                if (!this.has(key)) {
                  _context18.next = 5;
                  break;
                }

                return _context18.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getEndorsingRights(args, {
                  block: block
                });
                this.put(key, response);
                return _context18.abrupt("return", response);

              case 8:
              case "end":
                return _context18.stop();
            }
          }
        }, _callee18, this);
      }));
    }
    /**
     * @param options contains generic configuration for rpc calls
     *
     * @description Ballots casted so far during a voting period
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-ballot-list
     */

  }, {
    key: "getBallotList",
    value: function getBallotList() {
      var _ref19 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref19.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee19() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee19$(_context19) {
          while (1) {
            switch (_context19.prev = _context19.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBallotList', [block]);

                if (!this.has(key)) {
                  _context19.next = 5;
                  break;
                }

                return _context19.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBallotList({
                  block: block
                });
                this.put(key, response);
                return _context19.abrupt("return", response);

              case 8:
              case "end":
                return _context19.stop();
            }
          }
        }, _callee19, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Sum of ballots casted so far during a voting period.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-ballots
     */

  }, {
    key: "getBallots",
    value: function getBallots() {
      var _ref20 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref20.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee20() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee20$(_context20) {
          while (1) {
            switch (_context20.prev = _context20.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getBallots', [block]);

                if (!this.has(key)) {
                  _context20.next = 5;
                  break;
                }

                return _context20.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getBallots({
                  block: block
                });
                this.put(key, response);
                return _context20.abrupt("return", response);

              case 8:
              case "end":
                return _context20.stop();
            }
          }
        }, _callee20, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current period kind.
     *
     * @deprecated Deprecated in favor of getCurrentPeriod
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-period-kind
     */

  }, {
    key: "getCurrentPeriodKind",
    value: function getCurrentPeriodKind() {
      var _ref21 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref21.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee21() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee21$(_context21) {
          while (1) {
            switch (_context21.prev = _context21.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getCurrentPeriodKind', [block]);

                if (!this.has(key)) {
                  _context21.next = 5;
                  break;
                }

                return _context21.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getCurrentPeriodKind({
                  block: block
                });
                this.put(key, response);
                return _context21.abrupt("return", response);

              case 8:
              case "end":
                return _context21.stop();
            }
          }
        }, _callee21, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current proposal under evaluation.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-proposal
     */

  }, {
    key: "getCurrentProposal",
    value: function getCurrentProposal() {
      var _ref22 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref22.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee22() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee22$(_context22) {
          while (1) {
            switch (_context22.prev = _context22.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getCurrentProposal', [block]);

                if (!this.has(key)) {
                  _context22.next = 5;
                  break;
                }

                return _context22.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getCurrentProposal({
                  block: block
                });
                this.put(key, response);
                return _context22.abrupt("return", response);

              case 8:
              case "end":
                return _context22.stop();
            }
          }
        }, _callee22, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current expected quorum.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-quorum
     */

  }, {
    key: "getCurrentQuorum",
    value: function getCurrentQuorum() {
      var _ref23 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref23.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee23() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee23$(_context23) {
          while (1) {
            switch (_context23.prev = _context23.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getCurrentQuorum', [block]);

                if (!this.has(key)) {
                  _context23.next = 5;
                  break;
                }

                return _context23.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getCurrentQuorum({
                  block: block
                });
                this.put(key, response);
                return _context23.abrupt("return", response);

              case 8:
              case "end":
                return _context23.stop();
            }
          }
        }, _callee23, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List of delegates with their voting weight, in number of rolls.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-listings
     */

  }, {
    key: "getVotesListings",
    value: function getVotesListings() {
      var _ref24 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref24.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee24() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee24$(_context24) {
          while (1) {
            switch (_context24.prev = _context24.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getVotesListings', [block]);

                if (!this.has(key)) {
                  _context24.next = 5;
                  break;
                }

                return _context24.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getVotesListings({
                  block: block
                });
                this.put(key, response);
                return _context24.abrupt("return", response);

              case 8:
              case "end":
                return _context24.stop();
            }
          }
        }, _callee24, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List of proposals with number of supporters.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-proposals
     */

  }, {
    key: "getProposals",
    value: function getProposals() {
      var _ref25 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref25.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee25() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee25$(_context25) {
          while (1) {
            switch (_context25.prev = _context25.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getProposals', [block]);

                if (!this.has(key)) {
                  _context25.next = 5;
                  break;
                }

                return _context25.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getProposals({
                  block: block
                });
                this.put(key, response);
                return _context25.abrupt("return", response);

              case 8:
              case "end":
                return _context25.stop();
            }
          }
        }, _callee25, this);
      }));
    }
    /**
     *
     * @param data operation contents to forge
     * @param options contains generic configuration for rpc calls
     *
     * @description Forge an operation returning the unsigned bytes
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-forge-operations
     */

  }, {
    key: "forgeOperations",
    value: function forgeOperations(data) {
      var _ref26 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref26.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee26() {
        return _regeneratorRuntime.wrap(function _callee26$(_context26) {
          while (1) {
            switch (_context26.prev = _context26.next) {
              case 0:
                return _context26.abrupt("return", this.rpcClient.forgeOperations(data, {
                  block: block
                }));

              case 1:
              case "end":
                return _context26.stop();
            }
          }
        }, _callee26, this);
      }));
    }
    /**
     *
     * @param signedOpBytes signed bytes to inject
     *
     * @description Inject an operation in node and broadcast it. Returns the ID of the operation. The `signedOperationContents` should be constructed using a contextual RPCs from the latest block and signed by the client. By default, the RPC will wait for the operation to be (pre-)validated before answering. See RPCs under /blocks/prevalidation for more details on the prevalidation context.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-injection-operation
     */

  }, {
    key: "injectOperation",
    value: function injectOperation(signedOpBytes) {
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee27() {
        return _regeneratorRuntime.wrap(function _callee27$(_context27) {
          while (1) {
            switch (_context27.prev = _context27.next) {
              case 0:
                return _context27.abrupt("return", this.rpcClient.injectOperation(signedOpBytes));

              case 1:
              case "end":
                return _context27.stop();
            }
          }
        }, _callee27, this);
      }));
    }
    /**
     *
     * @param ops Operations to apply
     * @param options contains generic configuration for rpc calls
     *
     * @description Simulate the validation of an operation
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-preapply-operations
     */

  }, {
    key: "preapplyOperations",
    value: function preapplyOperations(ops) {
      var _ref27 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref27.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee28() {
        return _regeneratorRuntime.wrap(function _callee28$(_context28) {
          while (1) {
            switch (_context28.prev = _context28.next) {
              case 0:
                return _context28.abrupt("return", this.rpcClient.preapplyOperations(ops, {
                  block: block
                }));

              case 1:
              case "end":
                return _context28.stop();
            }
          }
        }, _callee28, this);
      }));
    }
    /**
     *
     * @param contract address of the contract we want to get the entrypoints of
     *
     * @description Return the list of entrypoints of the contract
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-entrypoints
     *
     * @version 005_PsBABY5H
     */

  }, {
    key: "getEntrypoints",
    value: function getEntrypoints(contract) {
      var _ref28 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref28.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee29() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee29$(_context29) {
          while (1) {
            switch (_context29.prev = _context29.next) {
              case 0:
                this.validateContract(contract);
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getEntrypoints', [block, contract]);

                if (!this.has(key)) {
                  _context29.next = 6;
                  break;
                }

                return _context29.abrupt("return", this.get(key));

              case 6:
                response = this.rpcClient.getEntrypoints(contract, {
                  block: block
                });
                this.put(key, response);
                return _context29.abrupt("return", response);

              case 9:
              case "end":
                return _context29.stop();
            }
          }
        }, _callee29, this);
      }));
    }
    /**
     * @param op Operation to run
     * @param options contains generic configuration for rpc calls
     *
     * @description Run an operation without signature checks
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-run-operation
     */

  }, {
    key: "runOperation",
    value: function runOperation(op) {
      var _ref29 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref29.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee30() {
        return _regeneratorRuntime.wrap(function _callee30$(_context30) {
          while (1) {
            switch (_context30.prev = _context30.next) {
              case 0:
                return _context30.abrupt("return", this.rpcClient.runOperation(op, {
                  block: block
                }));

              case 1:
              case "end":
                return _context30.stop();
            }
          }
        }, _callee30, this);
      }));
    }
    /**
     * @param code Code to run
     * @param options contains generic configuration for rpc calls
     *
     * @description Run a piece of code in the current context
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-run-code
     */

  }, {
    key: "runCode",
    value: function runCode(code) {
      var _ref30 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref30.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee31() {
        return _regeneratorRuntime.wrap(function _callee31$(_context31) {
          while (1) {
            switch (_context31.prev = _context31.next) {
              case 0:
                return _context31.abrupt("return", this.rpcClient.runCode(code, {
                  block: block
                }));

              case 1:
              case "end":
                return _context31.stop();
            }
          }
        }, _callee31, this);
      }));
    }
  }, {
    key: "getChainId",
    value: function getChainId() {
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee32() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee32$(_context32) {
          while (1) {
            switch (_context32.prev = _context32.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getChainId', []);

                if (!this.has(key)) {
                  _context32.next = 5;
                  break;
                }

                return _context32.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getChainId();
                this.put(key, response);
                return _context32.abrupt("return", response);

              case 8:
              case "end":
                return _context32.stop();
            }
          }
        }, _callee32, this);
      }));
    }
    /**
     *
     * @param data Data to pack
     * @param options contains generic configuration for rpc calls
     *
     * @description Computes the serialized version of a data expression using the same algorithm as script instruction PACK
     *
     * @example packData({ data: { string: "test" }, type: { prim: "string" } })
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-pack-data
     */

  }, {
    key: "packData",
    value: function packData(data) {
      var _ref31 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref31.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee33() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee33$(_context33) {
          while (1) {
            switch (_context33.prev = _context33.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'packData', [block, data]);

                if (!this.has(key)) {
                  _context33.next = 5;
                  break;
                }

                return _context33.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.packData(data, {
                  block: block
                });
                this.put(key, response);
                return _context33.abrupt("return", response);

              case 8:
              case "end":
                return _context33.stop();
            }
          }
        }, _callee33, this);
      }));
    }
    /**
     *
     * @description Return rpc root url
     */

  }, {
    key: "getRpcUrl",
    value: function getRpcUrl() {
      return this.rpcClient.getRpcUrl();
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Voting period of current block.
     *
     * @example getCurrentPeriod() will default to current voting period for /main/chains/block/head.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-period
     */

  }, {
    key: "getCurrentPeriod",
    value: function getCurrentPeriod() {
      var _ref32 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref32.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee34() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee34$(_context34) {
          while (1) {
            switch (_context34.prev = _context34.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getCurrentPeriod', [block]);

                if (!this.has(key)) {
                  _context34.next = 5;
                  break;
                }

                return _context34.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getCurrentPeriod({
                  block: block
                });
                this.put(key, response);
                return _context34.abrupt("return", response);

              case 8:
              case "end":
                return _context34.stop();
            }
          }
        }, _callee34, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Voting period of next block.
     *
     * @example getSuccessorPeriod() will default to successor voting period for /main/chains/block/head.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-successor-period
     */

  }, {
    key: "getSuccessorPeriod",
    value: function getSuccessorPeriod() {
      var _ref33 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref33.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee35() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee35$(_context35) {
          while (1) {
            switch (_context35.prev = _context35.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getSuccessorPeriod', [block]);

                if (!this.has(key)) {
                  _context35.next = 5;
                  break;
                }

                return _context35.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getSuccessorPeriod({
                  block: block
                });
                this.put(key, response);
                return _context35.abrupt("return", response);

              case 8:
              case "end":
                return _context35.stop();
            }
          }
        }, _callee35, this);
      }));
    }
    /**
     *
     * @param id Sapling state ID
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a sapling state ID.
     *
     * @see https://tezos.gitlab.io/active/rpc.html#get-block-id-context-sapling-sapling-state-id-get-diff
     */

  }, {
    key: "getSaplingDiffById",
    value: function getSaplingDiffById(id) {
      var _ref34 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref34.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee36() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee36$(_context36) {
          while (1) {
            switch (_context36.prev = _context36.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getSaplingDiffById', [block, id]);

                if (!this.has(key)) {
                  _context36.next = 5;
                  break;
                }

                return _context36.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getSaplingDiffById(id, {
                  block: block
                });
                this.put(key, response);
                return _context36.abrupt("return", response);

              case 8:
              case "end":
                return _context36.stop();
            }
          }
        }, _callee36, this);
      }));
    }
    /**
     *
     * @param contract address of the contract we want to get the sapling diff
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a sapling state.
     *
     * @see https://tezos.gitlab.io/active/rpc.html#get-block-id-context-contracts-contract-id-single-sapling-get-diff
     */

  }, {
    key: "getSaplingDiffByContract",
    value: function getSaplingDiffByContract(contract) {
      var _ref35 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref35.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee37() {
        var key, response;
        return _regeneratorRuntime.wrap(function _callee37$(_context37) {
          while (1) {
            switch (_context37.prev = _context37.next) {
              case 0:
                key = this.formatCacheKey(this.rpcClient.getRpcUrl(), 'getSaplingDiffByContract', [block, contract]);

                if (!this.has(key)) {
                  _context37.next = 5;
                  break;
                }

                return _context37.abrupt("return", this.get(key));

              case 5:
                response = this.rpcClient.getSaplingDiffByContract(contract, {
                  block: block
                });
                this.put(key, response);
                return _context37.abrupt("return", response);

              case 8:
              case "end":
                return _context37.stop();
            }
          }
        }, _callee37, this);
      }));
    }
  }]);

  return RpcClientCache;
}();

var OpKind;

(function (OpKind) {
  OpKind["ORIGINATION"] = "origination";
  OpKind["DELEGATION"] = "delegation";
  OpKind["REVEAL"] = "reveal";
  OpKind["TRANSACTION"] = "transaction";
  OpKind["ACTIVATION"] = "activate_account";
  OpKind["ENDORSEMENT"] = "endorsement";
  OpKind["ENDORSEMENT_WITH_SLOT"] = "endorsement_with_slot";
  OpKind["SEED_NONCE_REVELATION"] = "seed_nonce_revelation";
  OpKind["DOUBLE_ENDORSEMENT_EVIDENCE"] = "double_endorsement_evidence";
  OpKind["DOUBLE_BAKING_EVIDENCE"] = "double_baking_evidence";
  OpKind["PROPOSALS"] = "proposals";
  OpKind["BALLOT"] = "ballot";
  OpKind["FAILING_NOOP"] = "failing_noop";
  OpKind["REGISTER_GLOBAL_CONSTANT"] = "register_global_constant";
})(OpKind || (OpKind = {})); // IMPORTANT: THIS FILE IS AUTO GENERATED! DO NOT MANUALLY EDIT OR CHECKIN!


var VERSION = {
  "commitHash": "81f0a5b103f867f57fbe5d526315c375a3788346",
  "version": "11.2.0"
};
/***
 * @description RpcClient allows interaction with Tezos network through an rpc node
 */

var RpcClient = /*#__PURE__*/function () {
  /**
   *
   * @param url rpc root url
   * @param chain chain (default main)
   * @param httpBackend Http backend that issue http request.
   * You can override it by providing your own if you which to hook in the request/response
   *
   * @example new RpcClient('https://mainnet.api.tez.ie/', 'main') this will use https://mainnet.api.tez.ie//chains/main
   */
  function RpcClient(url) {
    var chain = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultChain;
    var httpBackend = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new HttpBackend();

    _classCallCheck(this, RpcClient);

    this.url = url;
    this.chain = chain;
    this.httpBackend = httpBackend;
  }

  _createClass(RpcClient, [{
    key: "createURL",
    value: function createURL(path) {
      // Trim trailing slashes because it is assumed to be included in path
      return "".concat(this.url.replace(/\/+$/g, '')).concat(path);
    }
  }, {
    key: "validateAddress",
    value: function validateAddress(address) {
      if (_validateAddress(address) !== ValidationResult.VALID) {
        throw new InvalidAddressError("Invalid address: ".concat(address));
      }
    }
  }, {
    key: "validateContract",
    value: function validateContract(address) {
      if (validateContractAddress(address) !== ValidationResult.VALID) {
        throw new InvalidAddressError("Invalid address: ".concat(address));
      }
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Get the block's hash, its unique identifier.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-hash
     */

  }, {
    key: "getBlockHash",
    value: function getBlockHash() {
      var _ref36 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref36.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee38() {
        var hash;
        return _regeneratorRuntime.wrap(function _callee38$(_context38) {
          while (1) {
            switch (_context38.prev = _context38.next) {
              case 0:
                _context38.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/hash")),
                  method: 'GET'
                });

              case 2:
                hash = _context38.sent;
                return _context38.abrupt("return", hash);

              case 4:
              case "end":
                return _context38.stop();
            }
          }
        }, _callee38, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List the ancestors of the given block which, if referred to as the branch in an operation header, are recent enough for that operation to be included in the current block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-live-blocks
     */

  }, {
    key: "getLiveBlocks",
    value: function getLiveBlocks() {
      var _ref37 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref37.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee39() {
        var blocks;
        return _regeneratorRuntime.wrap(function _callee39$(_context39) {
          while (1) {
            switch (_context39.prev = _context39.next) {
              case 0:
                _context39.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/live_blocks")),
                  method: 'GET'
                });

              case 2:
                blocks = _context39.sent;
                return _context39.abrupt("return", blocks);

              case 4:
              case "end":
                return _context39.stop();
            }
          }
        }, _callee39, this);
      }));
    }
    /**
     *
     * @param address address from which we want to retrieve the balance
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the balance of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-balance
     */

  }, {
    key: "getBalance",
    value: function getBalance(address) {
      var _ref38 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref38.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee40() {
        var balance;
        return _regeneratorRuntime.wrap(function _callee40$(_context40) {
          while (1) {
            switch (_context40.prev = _context40.next) {
              case 0:
                this.validateAddress(address);
                _context40.next = 3;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/balance")),
                  method: 'GET'
                });

              case 3:
                balance = _context40.sent;
                return _context40.abrupt("return", new BigNumber(balance));

              case 5:
              case "end":
                return _context40.stop();
            }
          }
        }, _callee40, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the storage
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the data of the contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-storage
     */

  }, {
    key: "getStorage",
    value: function getStorage(address) {
      var _ref39 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref39.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee41() {
        return _regeneratorRuntime.wrap(function _callee41$(_context41) {
          while (1) {
            switch (_context41.prev = _context41.next) {
              case 0:
                this.validateContract(address);
                return _context41.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/storage")),
                  method: 'GET'
                }));

              case 2:
              case "end":
                return _context41.stop();
            }
          }
        }, _callee41, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the script
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the code and data of the contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-script
     */

  }, {
    key: "getScript",
    value: function getScript(address) {
      var _ref40 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref40.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee42() {
        return _regeneratorRuntime.wrap(function _callee42$(_context42) {
          while (1) {
            switch (_context42.prev = _context42.next) {
              case 0:
                this.validateContract(address);
                return _context42.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/script")),
                  method: 'GET'
                }));

              case 2:
              case "end":
                return _context42.stop();
            }
          }
        }, _callee42, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the script
     * @param unparsingMode default is { unparsing_mode: "Readable" }
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the script of the contract and normalize it using the requested unparsing mode.
     *
     */

  }, {
    key: "getNormalizedScript",
    value: function getNormalizedScript(address) {
      var unparsingMode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        unparsing_mode: 'Readable'
      };

      var _ref41 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref41.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee43() {
        return _regeneratorRuntime.wrap(function _callee43$(_context43) {
          while (1) {
            switch (_context43.prev = _context43.next) {
              case 0:
                this.validateContract(address);
                return _context43.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/script/normalized")),
                  method: 'POST'
                }, unparsingMode));

              case 2:
              case "end":
                return _context43.stop();
            }
          }
        }, _callee43, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the complete status of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id
     */

  }, {
    key: "getContract",
    value: function getContract(address) {
      var _ref42 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref42.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee44() {
        var contractResponse;
        return _regeneratorRuntime.wrap(function _callee44$(_context44) {
          while (1) {
            switch (_context44.prev = _context44.next) {
              case 0:
                this.validateAddress(address);
                _context44.next = 3;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address)),
                  method: 'GET'
                });

              case 3:
                contractResponse = _context44.sent;
                return _context44.abrupt("return", Object.assign(Object.assign({}, contractResponse), {
                  balance: new BigNumber(contractResponse.balance)
                }));

              case 5:
              case "end":
                return _context44.stop();
            }
          }
        }, _callee44, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the manager
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the manager key of a contract.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-manager-key
     */

  }, {
    key: "getManagerKey",
    value: function getManagerKey(address) {
      var _ref43 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref43.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee45() {
        return _regeneratorRuntime.wrap(function _callee45$(_context45) {
          while (1) {
            switch (_context45.prev = _context45.next) {
              case 0:
                this.validateAddress(address);
                return _context45.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/manager_key")),
                  method: 'GET'
                }));

              case 2:
              case "end":
                return _context45.stop();
            }
          }
        }, _callee45, this);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the delegate (baker)
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the delegate of a contract, if any.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-delegate
     */

  }, {
    key: "getDelegate",
    value: function getDelegate(address) {
      var _ref44 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref44.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee46() {
        var delegate;
        return _regeneratorRuntime.wrap(function _callee46$(_context46) {
          while (1) {
            switch (_context46.prev = _context46.next) {
              case 0:
                this.validateAddress(address);
                _context46.prev = 1;
                _context46.next = 4;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/delegate")),
                  method: 'GET'
                });

              case 4:
                delegate = _context46.sent;
                _context46.next = 14;
                break;

              case 7:
                _context46.prev = 7;
                _context46.t0 = _context46["catch"](1);

                if (!(_context46.t0 instanceof HttpResponseError && _context46.t0.status === STATUS_CODE.NOT_FOUND)) {
                  _context46.next = 13;
                  break;
                }

                delegate = null;
                _context46.next = 14;
                break;

              case 13:
                throw _context46.t0;

              case 14:
                return _context46.abrupt("return", delegate);

              case 15:
              case "end":
                return _context46.stop();
            }
          }
        }, _callee46, this, [[1, 7]]);
      }));
    }
    /**
     *
     * @param address contract address from which we want to retrieve the big map key
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a key in the big map storage of the contract.
     *
     * @deprecated Deprecated in favor of getBigMapKeyByID
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-context-contracts-contract-id-big-map-get
     */

  }, {
    key: "getBigMapKey",
    value: function getBigMapKey(address, key) {
      var _ref45 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref45.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee47() {
        return _regeneratorRuntime.wrap(function _callee47$(_context47) {
          while (1) {
            switch (_context47.prev = _context47.next) {
              case 0:
                this.validateAddress(address);
                return _context47.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(address, "/big_map_get")),
                  method: 'POST'
                }, key));

              case 2:
              case "end":
                return _context47.stop();
            }
          }
        }, _callee47, this);
      }));
    }
    /**
     *
     * @param id Big Map ID
     * @param expr Expression hash to query (A b58check encoded Blake2b hash of the expression (The expression can be packed using the pack_data method))
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a key in a big map.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-big-maps-big-map-id-script-expr
     */

  }, {
    key: "getBigMapExpr",
    value: function getBigMapExpr(id, expr) {
      var _ref46 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultRPCOptions,
          block = _ref46.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee48() {
        return _regeneratorRuntime.wrap(function _callee48$(_context48) {
          while (1) {
            switch (_context48.prev = _context48.next) {
              case 0:
                return _context48.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/big_maps/").concat(id, "/").concat(expr)),
                  method: 'GET'
                }));

              case 1:
              case "end":
                return _context48.stop();
            }
          }
        }, _callee48, this);
      }));
    }
    /**
     *
     * @param address delegate address which we want to retrieve
     * @param options contains generic configuration for rpc calls
     *
     * @description Fetches information about a delegate from RPC.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-delegates-pkh
     */

  }, {
    key: "getDelegates",
    value: function getDelegates(address) {
      var _ref47 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref47.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee49() {
        var response;
        return _regeneratorRuntime.wrap(function _callee49$(_context49) {
          while (1) {
            switch (_context49.prev = _context49.next) {
              case 0:
                this.validateAddress(address);
                _context49.next = 3;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/delegates/").concat(address)),
                  method: 'GET'
                });

              case 3:
                response = _context49.sent;
                return _context49.abrupt("return", {
                  deactivated: response.deactivated,
                  balance: new BigNumber(response.balance),
                  frozen_balance: new BigNumber(response.frozen_balance),
                  frozen_balance_by_cycle: response.frozen_balance_by_cycle.map(function (_a) {
                    var deposit = _a.deposit,
                        deposits = _a.deposits,
                        fees = _a.fees,
                        rewards = _a.rewards,
                        rest = __rest(_a, ["deposit", "deposits", "fees", "rewards"]);

                    var castedToBigNumber = castToBigNumber({
                      deposit: deposit,
                      deposits: deposits,
                      fees: fees,
                      rewards: rewards
                    }, ['deposit', 'deposits', 'fees', 'rewards']);
                    return Object.assign(Object.assign({}, rest), {
                      deposit: castedToBigNumber.deposit,
                      deposits: castedToBigNumber.deposits,
                      fees: castedToBigNumber.fees,
                      rewards: castedToBigNumber.rewards
                    });
                  }),
                  staking_balance: new BigNumber(response.staking_balance),
                  delegated_contracts: response.delegated_contracts,
                  delegated_balance: new BigNumber(response.delegated_balance),
                  grace_period: response.grace_period,
                  voting_power: response.voting_power
                });

              case 5:
              case "end":
                return _context49.stop();
            }
          }
        }, _callee49, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description All constants
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-constants
     */

  }, {
    key: "getConstants",
    value: function getConstants() {
      var _ref48 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref48.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee50() {
        var response, castedResponse;
        return _regeneratorRuntime.wrap(function _callee50$(_context50) {
          while (1) {
            switch (_context50.prev = _context50.next) {
              case 0:
                _context50.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/constants")),
                  method: 'GET'
                });

              case 2:
                response = _context50.sent;
                castedResponse = castToBigNumber(response, ['time_between_blocks', 'hard_gas_limit_per_operation', 'hard_gas_limit_per_block', 'proof_of_work_threshold', 'tokens_per_roll', 'seed_nonce_revelation_tip', 'block_security_deposit', 'endorsement_security_deposit', 'block_reward', 'endorsement_reward', 'cost_per_byte', 'hard_storage_limit_per_operation', 'test_chain_duration', 'baking_reward_per_endorsement', 'delay_per_missing_endorsement', 'minimal_block_delay', 'liquidity_baking_subsidy', 'cache_layout', 'baking_reward_fixed_portion', 'baking_reward_bonus_per_slot', 'endorsing_reward_per_slot', 'double_baking_punishment', 'delay_increment_per_round']);
                return _context50.abrupt("return", Object.assign(Object.assign({}, response), castedResponse));

              case 5:
              case "end":
                return _context50.stop();
            }
          }
        }, _callee50, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls. See examples for various available sytaxes.
     *
     * @description All the information about a block
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id
     * @example getBlock() will default to /main/chains/block/head.
     * @example getBlock({ block: head~2 }) will return an offset of 2 blocks.
     * @example getBlock({ block: BL8fTiWcSxWCjiMVnDkbh6EuhqVPZzgWheJ2dqwrxYRm9AephXh~2 }) will return an offset of 2 blocks from given block hash..
     */

  }, {
    key: "getBlock",
    value: function getBlock() {
      var _ref49 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref49.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee51() {
        var response;
        return _regeneratorRuntime.wrap(function _callee51$(_context51) {
          while (1) {
            switch (_context51.prev = _context51.next) {
              case 0:
                _context51.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block)),
                  method: 'GET'
                });

              case 2:
                response = _context51.sent;
                return _context51.abrupt("return", response);

              case 4:
              case "end":
                return _context51.stop();
            }
          }
        }, _callee51, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description The whole block header
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-header
     */

  }, {
    key: "getBlockHeader",
    value: function getBlockHeader() {
      var _ref50 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref50.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee52() {
        var response;
        return _regeneratorRuntime.wrap(function _callee52$(_context52) {
          while (1) {
            switch (_context52.prev = _context52.next) {
              case 0:
                _context52.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/header")),
                  method: 'GET'
                });

              case 2:
                response = _context52.sent;
                return _context52.abrupt("return", response);

              case 4:
              case "end":
                return _context52.stop();
            }
          }
        }, _callee52, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description All the metadata associated to the block
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-metadata
     */

  }, {
    key: "getBlockMetadata",
    value: function getBlockMetadata() {
      var _ref51 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref51.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee53() {
        var response;
        return _regeneratorRuntime.wrap(function _callee53$(_context53) {
          while (1) {
            switch (_context53.prev = _context53.next) {
              case 0:
                _context53.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/metadata")),
                  method: 'GET'
                });

              case 2:
                response = _context53.sent;
                return _context53.abrupt("return", response);

              case 4:
              case "end":
                return _context53.stop();
            }
          }
        }, _callee53, this);
      }));
    }
    /**
     *
     * @param args contains optional query arguments
     * @param options contains generic configuration for rpc calls
     *
     * @description Retrieves the list of delegates allowed to bake a block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-helpers-baking-rights
     */

  }, {
    key: "getBakingRights",
    value: function getBakingRights() {
      var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _ref52 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref52.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee54() {
        var response;
        return _regeneratorRuntime.wrap(function _callee54$(_context54) {
          while (1) {
            switch (_context54.prev = _context54.next) {
              case 0:
                _context54.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/baking_rights")),
                  method: 'GET',
                  query: args
                });

              case 2:
                response = _context54.sent;
                return _context54.abrupt("return", response);

              case 4:
              case "end":
                return _context54.stop();
            }
          }
        }, _callee54, this);
      }));
    }
    /**
     *
     * @param args contains optional query arguments
     * @param options contains generic configuration for rpc calls
     *
     * @description Retrieves the list of delegates allowed to bake a block.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-helpers-endorsing-rights
     */

  }, {
    key: "getEndorsingRights",
    value: function getEndorsingRights() {
      var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _ref53 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref53.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee55() {
        var response;
        return _regeneratorRuntime.wrap(function _callee55$(_context55) {
          while (1) {
            switch (_context55.prev = _context55.next) {
              case 0:
                _context55.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/endorsing_rights")),
                  method: 'GET',
                  query: args
                });

              case 2:
                response = _context55.sent;
                return _context55.abrupt("return", response);

              case 4:
              case "end":
                return _context55.stop();
            }
          }
        }, _callee55, this);
      }));
    }
    /**
     * @param options contains generic configuration for rpc calls
     *
     * @description Ballots casted so far during a voting period
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-ballot-list
     */

  }, {
    key: "getBallotList",
    value: function getBallotList() {
      var _ref54 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref54.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee56() {
        var response;
        return _regeneratorRuntime.wrap(function _callee56$(_context56) {
          while (1) {
            switch (_context56.prev = _context56.next) {
              case 0:
                _context56.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/ballot_list")),
                  method: 'GET'
                });

              case 2:
                response = _context56.sent;
                return _context56.abrupt("return", response);

              case 4:
              case "end":
                return _context56.stop();
            }
          }
        }, _callee56, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Sum of ballots casted so far during a voting period.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-ballots
     */

  }, {
    key: "getBallots",
    value: function getBallots() {
      var _ref55 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref55.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee57() {
        var response;
        return _regeneratorRuntime.wrap(function _callee57$(_context57) {
          while (1) {
            switch (_context57.prev = _context57.next) {
              case 0:
                _context57.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/ballots")),
                  method: 'GET'
                });

              case 2:
                response = _context57.sent;
                return _context57.abrupt("return", response);

              case 4:
              case "end":
                return _context57.stop();
            }
          }
        }, _callee57, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current period kind.
     *
     * @deprecated Deprecated in favor of getCurrentPeriod
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-period-kind
     */

  }, {
    key: "getCurrentPeriodKind",
    value: function getCurrentPeriodKind() {
      var _ref56 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref56.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee58() {
        var response;
        return _regeneratorRuntime.wrap(function _callee58$(_context58) {
          while (1) {
            switch (_context58.prev = _context58.next) {
              case 0:
                _context58.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/current_period_kind")),
                  method: 'GET'
                });

              case 2:
                response = _context58.sent;
                return _context58.abrupt("return", response);

              case 4:
              case "end":
                return _context58.stop();
            }
          }
        }, _callee58, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current proposal under evaluation.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-proposal
     */

  }, {
    key: "getCurrentProposal",
    value: function getCurrentProposal() {
      var _ref57 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref57.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee59() {
        var response;
        return _regeneratorRuntime.wrap(function _callee59$(_context59) {
          while (1) {
            switch (_context59.prev = _context59.next) {
              case 0:
                _context59.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/current_proposal")),
                  method: 'GET'
                });

              case 2:
                response = _context59.sent;
                return _context59.abrupt("return", response);

              case 4:
              case "end":
                return _context59.stop();
            }
          }
        }, _callee59, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Current expected quorum.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-quorum
     */

  }, {
    key: "getCurrentQuorum",
    value: function getCurrentQuorum() {
      var _ref58 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref58.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee60() {
        var response;
        return _regeneratorRuntime.wrap(function _callee60$(_context60) {
          while (1) {
            switch (_context60.prev = _context60.next) {
              case 0:
                _context60.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/current_quorum")),
                  method: 'GET'
                });

              case 2:
                response = _context60.sent;
                return _context60.abrupt("return", response);

              case 4:
              case "end":
                return _context60.stop();
            }
          }
        }, _callee60, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List of delegates with their voting weight, in number of rolls.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-listings
     */

  }, {
    key: "getVotesListings",
    value: function getVotesListings() {
      var _ref59 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref59.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee61() {
        var response;
        return _regeneratorRuntime.wrap(function _callee61$(_context61) {
          while (1) {
            switch (_context61.prev = _context61.next) {
              case 0:
                _context61.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/listings")),
                  method: 'GET'
                });

              case 2:
                response = _context61.sent;
                return _context61.abrupt("return", response);

              case 4:
              case "end":
                return _context61.stop();
            }
          }
        }, _callee61, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description List of proposals with number of supporters.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-proposals
     */

  }, {
    key: "getProposals",
    value: function getProposals() {
      var _ref60 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref60.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee62() {
        var response;
        return _regeneratorRuntime.wrap(function _callee62$(_context62) {
          while (1) {
            switch (_context62.prev = _context62.next) {
              case 0:
                _context62.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/proposals")),
                  method: 'GET'
                });

              case 2:
                response = _context62.sent;
                return _context62.abrupt("return", response);

              case 4:
              case "end":
                return _context62.stop();
            }
          }
        }, _callee62, this);
      }));
    }
    /**
     *
     * @param data operation contents to forge
     * @param options contains generic configuration for rpc calls
     *
     * @description Forge an operation returning the unsigned bytes
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-forge-operations
     */

  }, {
    key: "forgeOperations",
    value: function forgeOperations(data) {
      var _ref61 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref61.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee63() {
        return _regeneratorRuntime.wrap(function _callee63$(_context63) {
          while (1) {
            switch (_context63.prev = _context63.next) {
              case 0:
                return _context63.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/forge/operations")),
                  method: 'POST'
                }, data));

              case 1:
              case "end":
                return _context63.stop();
            }
          }
        }, _callee63, this);
      }));
    }
    /**
     *
     * @param signedOpBytes signed bytes to inject
     *
     * @description Inject an operation in node and broadcast it. Returns the ID of the operation. The `signedOperationContents` should be constructed using a contextual RPCs from the latest block and signed by the client. By default, the RPC will wait for the operation to be (pre-)validated before answering. See RPCs under /blocks/prevalidation for more details on the prevalidation context.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-injection-operation
     */

  }, {
    key: "injectOperation",
    value: function injectOperation(signedOpBytes) {
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee64() {
        return _regeneratorRuntime.wrap(function _callee64$(_context64) {
          while (1) {
            switch (_context64.prev = _context64.next) {
              case 0:
                return _context64.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/injection/operation"),
                  method: 'POST'
                }, signedOpBytes));

              case 1:
              case "end":
                return _context64.stop();
            }
          }
        }, _callee64, this);
      }));
    }
    /**
     *
     * @param ops Operations to apply
     * @param options contains generic configuration for rpc calls
     *
     * @description Simulate the validation of an operation
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-preapply-operations
     */

  }, {
    key: "preapplyOperations",
    value: function preapplyOperations(ops) {
      var _ref62 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref62.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee65() {
        var response;
        return _regeneratorRuntime.wrap(function _callee65$(_context65) {
          while (1) {
            switch (_context65.prev = _context65.next) {
              case 0:
                _context65.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/preapply/operations")),
                  method: 'POST'
                }, ops);

              case 2:
                response = _context65.sent;
                return _context65.abrupt("return", response);

              case 4:
              case "end":
                return _context65.stop();
            }
          }
        }, _callee65, this);
      }));
    }
    /**
     *
     * @param contract address of the contract we want to get the entrypoints of
     *
     * @description Return the list of entrypoints of the contract
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-context-contracts-contract-id-entrypoints
     *
     * @version 005_PsBABY5H
     */

  }, {
    key: "getEntrypoints",
    value: function getEntrypoints(contract) {
      var _ref63 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref63.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee66() {
        var contractResponse;
        return _regeneratorRuntime.wrap(function _callee66$(_context66) {
          while (1) {
            switch (_context66.prev = _context66.next) {
              case 0:
                this.validateContract(contract);
                _context66.next = 3;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(contract, "/entrypoints")),
                  method: 'GET'
                });

              case 3:
                contractResponse = _context66.sent;
                return _context66.abrupt("return", contractResponse);

              case 5:
              case "end":
                return _context66.stop();
            }
          }
        }, _callee66, this);
      }));
    }
    /**
     * @param op Operation to run
     * @param options contains generic configuration for rpc calls
     *
     * @description Run an operation without signature checks
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-run-operation
     */

  }, {
    key: "runOperation",
    value: function runOperation(op) {
      var _ref64 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref64.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee67() {
        var response;
        return _regeneratorRuntime.wrap(function _callee67$(_context67) {
          while (1) {
            switch (_context67.prev = _context67.next) {
              case 0:
                _context67.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/scripts/run_operation")),
                  method: 'POST'
                }, op);

              case 2:
                response = _context67.sent;
                return _context67.abrupt("return", response);

              case 4:
              case "end":
                return _context67.stop();
            }
          }
        }, _callee67, this);
      }));
    }
    /**
     * @param code Code to run
     * @param options contains generic configuration for rpc calls
     *
     * @description Run a piece of code in the current context
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-run-code
     */

  }, {
    key: "runCode",
    value: function runCode(code) {
      var _ref65 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref65.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee68() {
        var response;
        return _regeneratorRuntime.wrap(function _callee68$(_context68) {
          while (1) {
            switch (_context68.prev = _context68.next) {
              case 0:
                _context68.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/scripts/run_code")),
                  method: 'POST'
                }, code);

              case 2:
                response = _context68.sent;
                return _context68.abrupt("return", response);

              case 4:
              case "end":
                return _context68.stop();
            }
          }
        }, _callee68, this);
      }));
    }
  }, {
    key: "getChainId",
    value: function getChainId() {
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee69() {
        return _regeneratorRuntime.wrap(function _callee69$(_context69) {
          while (1) {
            switch (_context69.prev = _context69.next) {
              case 0:
                return _context69.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/chain_id")),
                  method: 'GET'
                }));

              case 1:
              case "end":
                return _context69.stop();
            }
          }
        }, _callee69, this);
      }));
    }
    /**
     *
     * @param data Data to pack
     * @param options contains generic configuration for rpc calls
     *
     * @description Computes the serialized version of a data expression using the same algorithm as script instruction PACK
     * Note: You should always verify the packed bytes before signing or requesting that they be signed when using the the RPC to pack.
     * This precaution helps protect you and your applications users from RPC nodes that have been compromised.
     * A node that is operated by a bad actor, or compromised by a bad actor could return a fully formed operation that does not correspond to the input provided to the RPC endpoint.
     * A safer solution to pack and sign data would be to use the `packDataBytes` function available in the `@taquito/michel-codec` package.
     *
     * @example packData({ data: { string: "test" }, type: { prim: "string" } })
     *
     *
     * @see https://tezos.gitlab.io/api/rpc.html#post-block-id-helpers-scripts-pack-data
     */

  }, {
    key: "packData",
    value: function packData(data) {
      var _ref66 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref66.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee70() {
        var _a, gas, rest, formattedGas, tryBigNumber;

        return _regeneratorRuntime.wrap(function _callee70$(_context70) {
          while (1) {
            switch (_context70.prev = _context70.next) {
              case 0:
                _context70.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/helpers/scripts/pack_data")),
                  method: 'POST'
                }, data);

              case 2:
                _a = _context70.sent;
                gas = _a.gas;
                rest = __rest(_a, ["gas"]);
                formattedGas = gas;
                tryBigNumber = new BigNumber(gas || '');

                if (!tryBigNumber.isNaN()) {
                  formattedGas = tryBigNumber;
                }

                return _context70.abrupt("return", Object.assign({
                  gas: formattedGas
                }, rest));

              case 9:
              case "end":
                return _context70.stop();
            }
          }
        }, _callee70, this);
      }));
    }
    /**
     *
     * @description Return rpc root url
     */

  }, {
    key: "getRpcUrl",
    value: function getRpcUrl() {
      return this.url;
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Voting period of current block.
     *
     * @example getCurrentPeriod() will default to current voting period for /main/chains/block/head.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-current-period
     */

  }, {
    key: "getCurrentPeriod",
    value: function getCurrentPeriod() {
      var _ref67 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref67.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee71() {
        var response;
        return _regeneratorRuntime.wrap(function _callee71$(_context71) {
          while (1) {
            switch (_context71.prev = _context71.next) {
              case 0:
                _context71.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/current_period")),
                  method: 'GET'
                });

              case 2:
                response = _context71.sent;
                return _context71.abrupt("return", response);

              case 4:
              case "end":
                return _context71.stop();
            }
          }
        }, _callee71, this);
      }));
    }
    /**
     *
     * @param options contains generic configuration for rpc calls
     *
     * @description Voting period of next block.
     *
     * @example getSuccessorPeriod() will default to successor voting period for /main/chains/block/head.
     *
     * @see https://tezos.gitlab.io/api/rpc.html#get-block-id-votes-successor-period
     */

  }, {
    key: "getSuccessorPeriod",
    value: function getSuccessorPeriod() {
      var _ref68 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultRPCOptions,
          block = _ref68.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee72() {
        var response;
        return _regeneratorRuntime.wrap(function _callee72$(_context72) {
          while (1) {
            switch (_context72.prev = _context72.next) {
              case 0:
                _context72.next = 2;
                return this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/votes/successor_period")),
                  method: 'GET'
                });

              case 2:
                response = _context72.sent;
                return _context72.abrupt("return", response);

              case 4:
              case "end":
                return _context72.stop();
            }
          }
        }, _callee72, this);
      }));
    }
    /**
     *
     * @param id Sapling state ID
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a sapling state ID.
     *
     * @see https://tezos.gitlab.io/active/rpc.html#get-block-id-context-sapling-sapling-state-id-get-diff
     */

  }, {
    key: "getSaplingDiffById",
    value: function getSaplingDiffById(id) {
      var _ref69 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref69.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee73() {
        return _regeneratorRuntime.wrap(function _callee73$(_context73) {
          while (1) {
            switch (_context73.prev = _context73.next) {
              case 0:
                return _context73.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/sapling/").concat(id, "/get_diff")),
                  method: 'GET'
                }));

              case 1:
              case "end":
                return _context73.stop();
            }
          }
        }, _callee73, this);
      }));
    }
    /**
     *
     * @param contract address of the contract we want to get the sapling diff
     * @param options contains generic configuration for rpc calls
     *
     * @description Access the value associated with a sapling state.
     *
     * @see https://tezos.gitlab.io/active/rpc.html#get-block-id-context-contracts-contract-id-single-sapling-get-diff
     */

  }, {
    key: "getSaplingDiffByContract",
    value: function getSaplingDiffByContract(contract) {
      var _ref70 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultRPCOptions,
          block = _ref70.block;

      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee74() {
        return _regeneratorRuntime.wrap(function _callee74$(_context74) {
          while (1) {
            switch (_context74.prev = _context74.next) {
              case 0:
                return _context74.abrupt("return", this.httpBackend.createRequest({
                  url: this.createURL("/chains/".concat(this.chain, "/blocks/").concat(block, "/context/contracts/").concat(contract, "/single_sapling_get_diff")),
                  method: 'GET'
                }));

              case 1:
              case "end":
                return _context74.stop();
            }
          }
        }, _callee74, this);
      }));
    }
  }]);

  return RpcClient;
}();

export { OpKind, RpcClient, RpcClientCache, VERSION, castToBigNumber, defaultChain, defaultRPCOptions };